/**
 * IpoDetailPage — /ipo/:symbol
 *
 * Renders the full IPO analysis with 7 sections:
 *   1. Header (symbol, sector, IPO price, subscription dates, score)
 *   2. IPO Recommendation (signal, conviction, summary, reasons, risks)
 *   3. Valuation at IPO Price (vs sector + intrinsic values)
 *   4. 5-Year Financial History
 *   5. Specialist Views (business / financial / valuation / risk)
 *   6. Business Overview
 *   7. Post-Listing Tracking (only after the stock lists)
 */
import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { apiGet, formatNumber } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import LoadingState from "@/components/common/LoadingState";
import ErrorState from "@/components/common/ErrorState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowLeft, Sparkles, TrendingUp, Building2, ShieldAlert, Coins, DollarSign, AlertTriangle, ScanLine, Globe, Quote, Pencil, CheckCircle2 } from "lucide-react";
import IpoFinancialsVerifyDialog from "@/components/ipo/IpoFinancialsVerifyDialog";

const SIGNAL_CLASSES = {
  "STRONG SUBSCRIBE": "bg-[hsl(var(--success))] text-white",
  "SUBSCRIBE": "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border border-[hsl(var(--success))]/30",
  "WATCHLIST": "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border border-[hsl(var(--warning))]/40",
  "AVOID": "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border border-[hsl(var(--destructive))]/30",
};

// ---------- Emptiness helpers (Bug 2 fix) ----------
//
// Treat a value as "empty" when it's null/undefined, an empty string,
// an empty collection, or a dict where every child is one of those.
// This lets us render helpful guidance ("Upload the audited financials
// and re-run analysis") instead of blank sections when a prospectus
// yielded partial data.
const isEmptyValue = (v) => {
  if (v === null || v === undefined || v === "") return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") {
    return Object.values(v).every((x) => isEmptyValue(x));
  }
  return false;
};
const hasAnyData = (obj) => obj && !isEmptyValue(obj);

// Thin helper used by every section to render a standard "section
// couldn't be generated" tile with a Run-Full-Analysis CTA. Keeps the
// messaging consistent and saves the admin a trip to Data Sources.
const MissingSection = ({ message, hint, testId }) => {
  const { isAdmin } = useAuth();
  return (
    <div
      className="rounded-md border border-dashed bg-muted/30 px-4 py-5 text-center space-y-2"
      data-testid={testId}
    >
      <div className="text-sm font-medium">{message}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      {isAdmin && (
        <Button
          asChild
          size="sm"
          variant="outline"
          className="gap-1.5 mt-2"
          data-testid={`${testId}-cta`}
        >
          <Link to="/admin/market-intelligence#ipo">
            <Sparkles className="h-3.5 w-3.5" /> Run Full Analysis
          </Link>
        </Button>
      )}
    </div>
  );
};

const fmt = (n, d = 2) => (n == null ? "—" : formatNumber(n, d));
const fmtPct = (n, d = 1) => (n == null ? "—" : `${n > 0 ? "+" : ""}${formatNumber(n, d)}%`);
const fmtRs = (n, d = 2) => (n == null ? "—" : `Rs. ${formatNumber(n, d)}`);

const Section = ({ Icon, title, description, children, testId, headerAction }) => (
  <Card className="border bg-card" data-testid={testId}>
    <CardHeader>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-primary" />}
          <CardTitle className="font-display">{title}</CardTitle>
        </div>
        {headerAction}
      </div>
      {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const SignalPill = ({ signal, conviction }) => {
  if (!signal) return <Badge variant="outline">Pending</Badge>;
  const cls = SIGNAL_CLASSES[signal.toUpperCase()] || "bg-muted";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${cls}`}
      data-testid={`ipo-rec-signal-${signal.replace(/\s+/g, "-").toLowerCase()}`}
    >
      ★ {signal}
      {conviction && <span className="text-[10px] opacity-80 uppercase">· {conviction}</span>}
    </span>
  );
};

/** Format a date (ISO string or Date) like "15 May 2026". */
const fmtDate = (d) => {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return String(d);
  }
};

const HeaderCard = ({ ipo }) => {
  const det = ipo.ipo_details || {};
  const sc = ipo.scoring || {};
  const rec = ipo.ipo_recommendation || {};
  // We hide the 6-layer composite score badge for pre-listing / recent
  // IPOs because it's computed for fully-listed stocks with a year of
  // trading history, full balance-sheet data, and sector benchmarks.
  // For an IPO with missing BVPS / ROE the composite drops toward 20/100
  // which directly contradicts the IPO recommendation signal
  // (e.g. STRONG SUBSCRIBE at score 20). Until the stock has ≥ 12 months
  // of filed consolidated accounts the composite isn't meaningful.
  const listDate = det.listing_date ? new Date(det.listing_date) : null;
  const daysSinceListing = listDate
    ? Math.floor((Date.now() - listDate.getTime()) / (1000 * 60 * 60 * 24))
    : -1;
  const status = (det.ipo_status || "").toLowerCase();
  const isRecentIpo = ["upcoming", "open", "closed"].includes(status)
    || daysSinceListing < 0
    || daysSinceListing < 365;
  return (
    <Card className="border bg-card" data-testid="ipo-header-card">
      <CardContent className="pt-6 pb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="font-display font-semibold text-xl truncate">{ipo.company_name}</h1>
            <div className="text-sm text-muted-foreground mt-0.5">
              <span className="font-mono">{ipo.symbol}</span> · {ipo.sector || "Sector —"}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <SignalPill signal={rec.signal} conviction={rec.conviction} />
            {!isRecentIpo && (
              <Badge variant="outline" className="gap-1" data-testid="ipo-composite-score">
                Score <span className="font-semibold">{sc.composite_score ?? "—"}</span>
                {sc.signal ? <span>· {sc.signal}</span> : null}
              </Badge>
            )}
            {sc.data_confidence && (
              <Badge variant="secondary" className="text-[10px] uppercase">
                Data: {sc.data_confidence}
              </Badge>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">IPO price</div>
            <div className="font-semibold">{fmtRs(det.ipo_price)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Subscription</div>
            <div className="font-semibold text-xs">
              {fmtDate(det.subscription_opens)} – {fmtDate(det.subscription_closes)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Listing date</div>
            <div className="font-semibold text-xs">{fmtDate(det.listing_date)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Market cap @ IPO</div>
            <div className="font-semibold">{det.market_cap_at_ipo ? fmtRs(det.market_cap_at_ipo / 1e9, 2) + " Bn" : "—"}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const RecommendationCard = ({ ipo }) => {
  const rec = ipo.ipo_recommendation || {};
  const hasRec = hasAnyData(rec) && (rec.signal || rec.summary);
  return (
    <Section
      Icon={Sparkles}
      title="IPO Recommendation"
      description="Signal, conviction, and plain-english summary for Sri Lankan retail investors."
      testId="ipo-recommendation-card"
    >
      {!hasRec ? (
        <MissingSection
          message="IPO recommendation not yet available."
          hint="Run the full analysis to generate the SUBSCRIBE / WATCHLIST / AVOID signal, summary, and 12-month target."
          testId="ipo-recommendation-missing"
        />
      ) : (
      <>
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <SignalPill signal={rec.signal} conviction={rec.conviction} />
        {rec.target_price_12m != null && (
          <Badge variant="outline" className="gap-1 text-xs">
            12-month target {fmtRs(rec.target_price_12m)}{" "}
            {rec.upside_to_target_pct != null && (
              <span className="text-[hsl(var(--success))]">({fmtPct(rec.upside_to_target_pct)})</span>
            )}
          </Badge>
        )}
        {rec.time_horizon && (
          <Badge variant="secondary" className="text-[10px]">Horizon: {rec.time_horizon}</Badge>
        )}
      </div>
      {rec.summary ? (
        <div className="prose prose-sm max-w-none dark:prose-invert" data-testid="ipo-rec-summary">
          {rec.summary.split(/\n\n+/).map((p, i) => (
            <p key={i} className="text-sm text-foreground leading-relaxed">{p}</p>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Summary not yet generated.</p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            Key reasons {rec.signal?.toLowerCase().includes("subscribe") ? "to subscribe" : ""}
          </div>
          <ul className="space-y-1 text-sm" data-testid="ipo-rec-reasons">
            {(rec.key_reasons || []).map((r, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="text-[hsl(var(--success))]">✓</span>
                <span>{r}</span>
              </li>
            ))}
            {(!rec.key_reasons || rec.key_reasons.length === 0) && (
              <li className="text-xs text-muted-foreground">—</li>
            )}
          </ul>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Key risks</div>
          <ul className="space-y-1 text-sm" data-testid="ipo-rec-risks">
            {(rec.key_risks || []).map((r, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--warning))] shrink-0 mt-0.5" />
                <span>{r}</span>
              </li>
            ))}
            {(!rec.key_risks || rec.key_risks.length === 0) && (
              <li className="text-xs text-muted-foreground">—</li>
            )}
          </ul>
        </div>
      </div>
      {rec.entry_guidance && (
        <div className="mt-4 rounded-md border bg-muted/40 px-3 py-2.5">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            Entry guidance
          </div>
          <p className="text-sm">{rec.entry_guidance}</p>
        </div>
      )}
      </>
      )}
    </Section>
  );
};

const ValuationTable = ({ ipo }) => {
  const v = ipo.valuation_at_ipo_price || {};
  const det = ipo.ipo_details || {};
  const hasValuation = hasAnyData(v);
  if (!hasValuation) {
    return (
      <Section
        Icon={Coins}
        title={`Valuation at Rs. ${fmt(det.ipo_price)}`}
        description="PE / PB / Graham / DDM derived from the latest financials."
        testId="ipo-valuation-card"
      >
        <MissingSection
          message="Valuation data not available."
          hint="Valuation metrics are derived from the IPO price + latest EPS/BVPS/DPS. Upload the audited financial statements and the valuation report, then re-run the full analysis."
          testId="ipo-valuation-missing"
        />
      </Section>
    );
  }
  const rows = [
    { label: "PE ratio", ours: v.pe_ratio, sector: v.sector_pe, deltaPct: v.pe_vs_sector_pct, suffix: "x" },
    { label: "PB ratio", ours: v.pb_ratio, sector: v.sector_pb, deltaPct: v.pb_vs_sector_pct, suffix: "x" },
    {
      label: "Dividend yield",
      ours: v.dividend_yield != null ? v.dividend_yield * 100 : null,
      sector: null,
      deltaPct: null,
      suffix: "%",
      // When the backend deliberately suppressed div yield (pre-listing
      // IPO or <12 months since listing) we show an explicit N/A and a
      // one-line explanation instead of a dash. Prevents the misleading
      // subsidiary-derived yield showing up on the IPO card.
      suppressedNote: v.dividend_yield_suppressed
        ? "No dividend declared yet as a listed company — first expected after year 1 of consolidated accounts."
        : null,
    },
  ];
  return (
    <Section
      Icon={Coins}
      title={`Valuation at Rs. ${fmt(det.ipo_price)}`}
      description="Ratios at the IPO price vs sector benchmarks. Intrinsic value estimates use Graham Number and Gordon Growth DDM."
      testId="ipo-valuation-card"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="ipo-valuation-table">
          <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
            <tr className="border-b">
              <th className="text-left py-2">Metric</th>
              <th className="text-right py-2">IPO</th>
              <th className="text-right py-2">Sector</th>
              <th className="text-right py-2">vs Sector</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-b last:border-0">
                <td className="py-2">
                  {r.label}
                  {r.suppressedNote && (
                    <div className="text-[10px] text-muted-foreground mt-0.5 font-normal max-w-[420px]">
                      {r.suppressedNote}
                    </div>
                  )}
                </td>
                <td className="text-right py-2 font-mono">
                  {r.suppressedNote
                    ? <span className="text-muted-foreground italic">N/A</span>
                    : r.ours != null ? `${fmt(r.ours)}${r.suffix}` : "—"}
                </td>
                <td className="text-right py-2 font-mono">{r.sector != null ? `${fmt(r.sector)}${r.suffix}` : "—"}</td>
                <td className={`text-right py-2 font-mono ${r.deltaPct != null && r.deltaPct <= 0 ? "text-[hsl(var(--success))]" : r.deltaPct != null ? "text-[hsl(var(--destructive))]" : ""}`}>
                  {r.deltaPct != null ? fmtPct(r.deltaPct) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div className="rounded-md border p-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Graham Number</div>
          <div className="font-semibold text-lg">{fmtRs(v.graham_number)}</div>
          <div className="text-xs text-muted-foreground">
            vs IPO price:{" "}
            <span className={v.graham_upside_pct > 0 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]"}>
              {fmtPct(v.graham_upside_pct)}
            </span>
          </div>
        </div>
        <div className="rounded-md border p-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">DDM Fair Value</div>
          <div className="text-sm">
            Conservative: <span className="font-mono">{fmtRs(v.ddm_value_conservative)}</span>
          </div>
          <div className="text-sm">
            Base: <span className="font-mono">{fmtRs(v.ddm_value_base)}</span>
          </div>
          <div className="text-sm">
            Optimistic: <span className="font-mono">{fmtRs(v.ddm_value_optimistic)}</span>
          </div>
        </div>
      </div>
      {(det.funds_to_be_raised || det.use_of_proceeds) && (
        <div className="mt-4 rounded-md border bg-muted/40 px-3 py-2.5 text-xs">
          {det.funds_to_be_raised && (
            <div>
              <span className="text-muted-foreground">Funds raised:</span>{" "}
              <span className="font-semibold">{fmtRs(det.funds_to_be_raised / 1e6, 1)} Mn</span>
            </div>
          )}
          {det.use_of_proceeds && (
            <div className="mt-1">
              <span className="text-muted-foreground">Use of proceeds:</span> {det.use_of_proceeds}
            </div>
          )}
        </div>
      )}
    </Section>
  );
};

const FinancialsTable = ({ ipo, onEditClick, adminVerifiedAt }) => {
  const f = ipo.financials || {};
  const pipeline = ipo.pipeline || {};
  const visionExtracted = !!pipeline.vision_extracted;
  // Pull balance-sheet fallbacks from the Perplexity web-research
  // block. When the scanned audited financials don't yield BVPS / D-E
  // (balance sheet pages often live outside our 25-page Vision
  // window for holding companies), the Simply Wall St + CSE filings
  // summary captured by the research agent typically has enough to
  // compute these deterministically. We annotate each derived value
  // with a small "via research" pill so the provenance is clear.
  const wr = (ipo.web_research || {}).data || {};
  const bsTtm = (wr.financials || {}).balance_sheet_ttm || {};
  const det = ipo.ipo_details || {};
  const totalShares = det.total_shares_post_ipo || null;
  const fallbackBvps =
    bsTtm.equity_lkr_bn != null && totalShares
      ? (bsTtm.equity_lkr_bn * 1e9) / totalShares
      : null;
  const fallbackDebtToEquity =
    bsTtm.total_debt_lkr_bn != null && bsTtm.equity_lkr_bn
      ? bsTtm.total_debt_lkr_bn / bsTtm.equity_lkr_bn
      : null;
  const years = useMemo(() => {
    const keys = new Set();
    ["revenue", "net_profit", "eps", "bvps", "roe", "dps"].forEach((k) => {
      Object.keys(f[k] || {}).forEach((y) => keys.add(y));
    });
    return Array.from(keys).sort();
  }, [f]);
  // Detect implausible YoY revenue swings (>= ±30%) as a proxy for
  // Vision OCR misreads. JXG's 2021→2025 shows -17% / +111% / +70% /
  // -35% — that pattern triggers an accuracy banner so the reader
  // knows to verify against the original PDFs before relying on the
  // trend.
  const revenueSpikes = useMemo(() => {
    const rev = f.revenue || {};
    const sortedYears = Object.keys(rev).sort();
    let extremeSwings = 0;
    for (let i = 1; i < sortedYears.length; i += 1) {
      const prev = rev[sortedYears[i - 1]];
      const curr = rev[sortedYears[i]];
      if (!prev || !curr) continue;
      const pct = Math.abs((curr - prev) / prev);
      if (pct >= 0.3) extremeSwings += 1;
    }
    return extremeSwings;
  }, [f]);
  const showRevenueWarning = visionExtracted && revenueSpikes >= 2;
  if (years.length === 0) {
    return (
      <Section Icon={TrendingUp} title="5-Year Financial Track Record" testId="ipo-financials-card">
        <MissingSection
          message="Financial data could not be extracted."
          hint="Please ensure the Audited Financial Statements are uploaded as a supporting document, then re-run the full analysis."
          testId="ipo-financials-missing"
        />
      </Section>
    );
  }
  const rowDefs = [
    { label: "Revenue (Rs. Mn)", key: "revenue", scale: 1e6, decimals: 0 },
    { label: "Net profit (Rs. Mn)", key: "net_profit", scale: 1e6, decimals: 0 },
    { label: "EPS (Rs.)", key: "eps", scale: 1, decimals: 2 },
    { label: "BVPS (Rs.)", key: "bvps", scale: 1, decimals: 2 },
    { label: "ROE (%)", key: "roe", scale: 1, decimals: 1 },
    { label: "DPS (Rs.)", key: "dps", scale: 1, decimals: 2 },
  ];
  return (
    <Section
      Icon={TrendingUp}
      title="5-Year Financial Track Record"
      description={`Revenue CAGR: ${f.revenue_cagr_5yr != null ? `${f.revenue_cagr_5yr.toFixed(1)}%` : "—"} · EPS CAGR: ${f.eps_cagr_5yr != null ? `${f.eps_cagr_5yr.toFixed(1)}%` : "—"}`}
      testId="ipo-financials-card"
      headerAction={onEditClick ? (
        <div className="flex items-center gap-2">
          {adminVerifiedAt && (
            <Badge
              variant="outline"
              className="gap-1 text-[10px] border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]"
              data-testid="ipo-financials-admin-verified-badge"
              title={`Admin verified at ${new Date(adminVerifiedAt).toLocaleString()}`}
            >
              <CheckCircle2 className="h-3 w-3" /> Admin verified
            </Badge>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={onEditClick}
            className="gap-1.5"
            data-testid="ipo-financials-edit-button"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit Financial Data
          </Button>
        </div>
      ) : null}
    >
      {showRevenueWarning && (
        <div
          className="mb-3 flex items-start gap-2 rounded-md border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 px-3 py-2 text-xs text-[hsl(var(--warning-foreground,var(--warning)))]"
          data-testid="ipo-financials-vision-warning"
        >
          <ScanLine className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="space-y-0.5">
            <div className="font-semibold text-foreground">
              Figures extracted via AI Vision from scanned documents
            </div>
            <div className="text-muted-foreground">
              Revenue volatility below may reflect group restructuring (Janashakthi Group
              consolidated several subsidiaries during this period) or Vision extraction
              variance on scanned pages. Verify all figures against the original audited
              financial statements before investing.
            </div>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="ipo-financials-table">
          <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
            <tr className="border-b">
              <th className="text-left py-2">Metric</th>
              {years.map((y) => (
                <th key={y} className="text-right py-2 font-mono">{y}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowDefs.map((r) => (
              <tr key={r.key} className="border-b last:border-0">
                <td className="py-2">{r.label}</td>
                {years.map((y) => {
                  const raw = (f[r.key] || {})[y];
                  const scaled = raw != null ? (r.pct ? raw * 100 : raw / r.scale) : null;
                  return (
                    <td key={y} className="text-right py-2 font-mono">
                      {scaled == null ? "—" : fmt(scaled, r.decimals)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
        <div>
          <span className="text-muted-foreground">Debt/Equity:</span>{" "}
          <span className="font-semibold">
            {f.debt_to_equity != null
              ? fmt(f.debt_to_equity)
              : fallbackDebtToEquity != null
                ? fmt(fallbackDebtToEquity)
                : "—"}
          </span>
          {f.debt_to_equity == null && fallbackDebtToEquity != null && (
            <span className="ml-2 text-[10px] text-muted-foreground italic">via research</span>
          )}
        </div>
        <div>
          <span className="text-muted-foreground">Current ratio:</span>{" "}
          <span className="font-semibold">{fmt(f.current_ratio)}</span>
        </div>
        {fallbackBvps != null && (
          <div className="col-span-2">
            <span className="text-muted-foreground">BVPS (implied, TTM):</span>{" "}
            <span className="font-semibold">Rs. {fmt(fallbackBvps, 2)}</span>
            <span className="ml-2 text-[10px] text-muted-foreground italic">
              via research (equity {fmt(bsTtm.equity_lkr_bn, 2)} Bn ÷ {fmt(totalShares / 1e9, 2)} Bn shares)
            </span>
          </div>
        )}
        {bsTtm.total_assets_lkr_bn != null && (
          <div>
            <span className="text-muted-foreground">Total assets (TTM):</span>{" "}
            <span className="font-semibold">Rs. {fmt(bsTtm.total_assets_lkr_bn, 1)} Bn</span>
          </div>
        )}
        {bsTtm.total_debt_lkr_bn != null && (
          <div>
            <span className="text-muted-foreground">Total debt (TTM):</span>{" "}
            <span className="font-semibold">Rs. {fmt(bsTtm.total_debt_lkr_bn, 1)} Bn</span>
          </div>
        )}
      </div>
    </Section>
  );
};

const SpecialistViews = ({ ipo }) => {
  const v = ipo.specialist_views || {};
  const items = [
    { key: "valuation_assessment", title: "Valuation at IPO price", Icon: DollarSign, data: v.valuation_assessment },
    { key: "business_quality", title: "Business quality", Icon: Building2, data: v.business_quality },
    { key: "financial_quality", title: "Financial track record", Icon: TrendingUp, data: v.financial_quality },
    { key: "risk_assessment", title: "Risk assessment", Icon: ShieldAlert, data: v.risk_assessment },
  ];
  const hasAny = items.some((it) => hasAnyData(it.data));
  if (!hasAny) {
    return (
      <Section Icon={Sparkles} title="Specialist Views" testId="ipo-specialists-card">
        <MissingSection
          message="Specialist views not yet available."
          hint="The four specialist panels (valuation, business, financials, risk) are generated by the coordinator pass of the analysis pipeline. Run the full analysis to populate them."
          testId="ipo-specialists-missing"
        />
      </Section>
    );
  }
  return (
    <Section Icon={Sparkles} title="Specialist Views" testId="ipo-specialists-card">
      <Accordion type="multiple" className="w-full">
        {items.map(({ key, title, Icon: I, data }) => (
          <AccordionItem key={key} value={key}>
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <I className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">{title}</span>
                {data?.verdict && (
                  <Badge variant="outline" className="text-[10px]" data-testid={`ipo-specialist-${key}-verdict`}>
                    {data.verdict}
                  </Badge>
                )}
                {/* Deterministic-fallback badge: when the coordinator
                    hasn't produced a qualitative verdict, derive one
                    from the numeric `score` so the trigger still
                    carries a visual cue. */}
                {!data?.verdict && typeof data?.score === "number" && (
                  <Badge variant="secondary" className="text-[10px] font-mono" data-testid={`ipo-specialist-${key}-score`}>
                    {data.score}/10
                  </Badge>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-1 text-sm space-y-2">
              {!data && <div className="text-xs text-muted-foreground">Not yet generated.</div>}
              {/* Deterministic fallback: when the coordinator is
                  unavailable (Emergent Claude 502s) the pipeline still
                  emits a minimal {score, narrative} shape. Render that
                  as the primary summary so users always have something
                  useful to read even during outages. */}
              {data?.narrative && !data?.summary && (
                <p data-testid={`ipo-specialist-${key}-narrative`}>{data.narrative}</p>
              )}
              {typeof data?.score === "number" && !data?.verdict && (
                <div className="text-[11px] text-muted-foreground">
                  Score: <span className="font-semibold text-foreground">{data.score}/10</span>
                </div>
              )}
              {data?.summary && <p>{data.summary}</p>}
              {data?.revenue_trend && <p><strong>Revenue:</strong> {data.revenue_trend}</p>}
              {data?.profitability_trend && <p><strong>Profitability:</strong> {data.profitability_trend}</p>}
              {data?.balance_sheet_health && <p><strong>Balance sheet:</strong> {data.balance_sheet_health}</p>}
              {data?.pe_assessment && <p><strong>PE:</strong> {data.pe_assessment}</p>}
              {data?.graham_assessment && <p><strong>Graham:</strong> {data.graham_assessment}</p>}
              {data?.ddm_assessment && <p><strong>DDM:</strong> {data.ddm_assessment}</p>}
              {(data?.key_strengths?.length || 0) > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Strengths</div>
                  <ul className="list-disc pl-5 text-xs">
                    {data.key_strengths.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}
              {(data?.key_risks?.length || 0) > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Risks</div>
                  <ul className="list-disc pl-5 text-xs">
                    {data.key_risks.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}
              {(data?.mitigants?.length || 0) > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Mitigants</div>
                  <ul className="list-disc pl-5 text-xs">
                    {data.mitigants.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}
              {data?.competitive_moat && <p><strong>Moat:</strong> {data.competitive_moat}</p>}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Section>
  );
};

// ---------------- Web research (Perplexity sonar-pro) ----------------
// Shows the verified third-party facts that Claude's coordinator used
// when building the recommendation. Renders only when at least one
// substantial field was returned — otherwise we hide the card entirely
// so we never show an empty "Web research" block.
const WebResearchCard = ({ ipo }) => {
  const wr = ipo.web_research;
  if (!wr || wr.status === "failed" || !wr.data) return null;
  const d = wr.data || {};
  const kvf = Array.isArray(d.key_verified_facts) ? d.key_verified_facts : [];
  const subs = d.business_structure?.subsidiaries || [];
  const val = d.valuation || {};
  const mkt = d.market_reception || {};
  const basics = d.company_basics || {};
  const sources = wr.sources || [];

  // If we have literally nothing to show, bail out.
  const hasContent = kvf.length > 0 || subs.length > 0
    || val.valuer || mkt.oversubscribed_times
    || basics.description || sources.length > 0;
  if (!hasContent) return null;

  return (
    <Section
      title="Verified web research"
      icon={Globe}
      description={`${wr.queries_succeeded || 0}/${wr.queries_total || 0} Perplexity queries · ${sources.length} sources · ${wr.model || "sonar-pro"}`}
      testId="ipo-web-research-card"
    >
      <div className="space-y-5">
        {kvf.length > 0 && (
          <div data-testid="ipo-web-research-key-facts">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Key verified facts
            </div>
            <ul className="space-y-1.5">
              {kvf.slice(0, 10).map((fact, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Quote className="h-3.5 w-3.5 text-primary mt-1 shrink-0" />
                  <span>{fact}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(val.valuer || val.fair_value_per_share) && (
          <div
            className="rounded-md border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5 p-4"
            data-testid="ipo-web-research-valuation"
          >
            <div className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--success))] mb-1.5">
              Independent valuation
            </div>
            <div className="text-sm space-y-1">
              {val.valuer && (
                <div><span className="text-muted-foreground">Valuer:</span>{" "}<span className="font-medium">{val.valuer}</span></div>
              )}
              {val.fair_value_per_share && (
                <div><span className="text-muted-foreground">Fair value per share:</span>{" "}<span className="font-semibold">Rs. {val.fair_value_per_share}</span></div>
              )}
              {val.discount_to_fair_value_pct != null && (
                <div><span className="text-muted-foreground">IPO discount to fair value:</span>{" "}<span className="font-semibold text-[hsl(var(--success))]">{val.discount_to_fair_value_pct}%</span></div>
              )}
              {val.valuation_method && (
                <div className="text-xs text-muted-foreground mt-1">Method: {val.valuation_method}</div>
              )}
            </div>
          </div>
        )}

        {subs.length > 0 && (
          <div data-testid="ipo-web-research-subsidiaries">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Subsidiaries ({subs.length})
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {subs.map((s, i) => (
                <div key={i} className="rounded-md border p-3 bg-card/40">
                  <div className="font-medium text-sm">{s.name || `Subsidiary ${i + 1}`}</div>
                  {s.description && (
                    <div className="text-xs text-muted-foreground mt-1">{s.description}</div>
                  )}
                  {(s.years_active || s.notable_position) && (
                    <div className="text-xs mt-1.5">
                      {s.years_active && <span className="text-muted-foreground">{s.years_active}</span>}
                      {s.years_active && s.notable_position && <span className="text-muted-foreground"> · </span>}
                      {s.notable_position && <span className="font-medium">{s.notable_position}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {(mkt.oversubscribed_times || mkt.notable_size || mkt.first_day_close) && (
          <div
            className="rounded-md border border-[hsl(var(--info))]/30 bg-[hsl(var(--info))]/5 p-4"
            data-testid="ipo-web-research-reception"
          >
            <div className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--info))] mb-1.5">
              Market reception
            </div>
            <div className="text-sm space-y-1">
              {mkt.oversubscribed_times && (
                <div><span className="text-muted-foreground">Oversubscription:</span>{" "}<span className="font-semibold">{mkt.oversubscribed_times}</span></div>
              )}
              {mkt.notable_size && (
                <div><span className="text-muted-foreground">Size:</span> {mkt.notable_size}</div>
              )}
              {mkt.first_day_close && (
                <div><span className="text-muted-foreground">First-day status:</span> {mkt.first_day_close}</div>
              )}
              {mkt.analyst_sentiment && (
                <div className="text-xs text-muted-foreground mt-1">{mkt.analyst_sentiment}</div>
              )}
            </div>
          </div>
        )}

        {sources.length > 0 && (
          <div data-testid="ipo-web-research-sources">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Sources ({sources.length})
            </div>
            <div className="flex flex-wrap gap-1.5">
              {sources.slice(0, 12).map((url, i) => {
                let host = "";
                try { host = new URL(url).hostname.replace(/^www\./, ""); } catch (_) { host = url; }
                return (
                  <a
                    key={i}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs px-2 py-1 rounded border bg-card/40 hover:bg-muted transition-colors"
                    title={url}
                    data-testid={`ipo-web-research-source-${i}`}
                  >
                    {host.slice(0, 40)}
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
};

const BusinessOverview = ({ ipo }) => {
  const b = ipo.business_analysis || {};
  const hasBiz = hasAnyData(b);
  if (!hasBiz) {
    return (
      <Section Icon={Building2} title={`About ${ipo.company_name}`} testId="ipo-business-card">
        <MissingSection
          message="Business overview not yet available."
          hint="Upload the main prospectus PDF (not just the financial statements) so Claude can extract the business description, competitive advantages, and growth strategy."
          testId="ipo-business-missing"
        />
      </Section>
    );
  }
  return (
    <Section Icon={Building2} title={`About ${ipo.company_name}`} testId="ipo-business-card">
      {b.description && <p className="text-sm">{b.description}</p>}
      {b.business_model && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Business model</div>
          <p className="text-sm">{b.business_model}</p>
        </div>
      )}
      {(b.competitive_advantages?.length || 0) > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Competitive advantages</div>
          <ul className="list-disc pl-5 text-sm">
            {b.competitive_advantages.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}
      {b.growth_strategy && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Growth strategy</div>
          <p className="text-sm">{b.growth_strategy}</p>
        </div>
      )}
      {(b.major_shareholders?.length || 0) > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Major shareholders</div>
          <ul className="text-sm">
            {b.major_shareholders.map((s, i) => (
              <li key={i} className="font-mono text-xs">
                {typeof s === "string" ? s : `${s.name || "—"}${s.percent != null ? ` · ${fmt(s.percent * 100)}%` : ""}`}
              </li>
            ))}
          </ul>
        </div>
      )}
      {b.use_of_ipo_proceeds && (
        <div className="mt-3 rounded-md border bg-muted/40 p-3 text-xs">
          <div className="font-semibold mb-1">Use of IPO proceeds</div>
          <p>{b.use_of_ipo_proceeds}</p>
        </div>
      )}
    </Section>
  );
};

const PostListingCard = ({ ipo }) => {
  const t = ipo.post_listing_tracking || {};
  const det = ipo.ipo_details || {};
  const status = (det.ipo_status || "").toLowerCase();
  if (status !== "listed" && !t.listing_price) return null;
  const retPct = t.return_since_listing_pct;
  const correct = t.recommendation_correct;
  return (
    <Section Icon={TrendingUp} title="Post-Listing Performance" testId="ipo-post-listing-card">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Our call</div>
          <div className="font-semibold">{t.our_recommendation || "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">IPO price</div>
          <div className="font-semibold">{fmtRs(det.ipo_price)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Listing price</div>
          <div className="font-semibold">{fmtRs(t.listing_price)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Current price</div>
          <div className="font-semibold">{fmtRs(t.current_price)}</div>
        </div>
      </div>
      {retPct != null && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Badge className={retPct >= 0 ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30" : "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30"}>
            Return since listing: {fmtPct(retPct)}
          </Badge>
          {correct !== null && correct !== undefined && (
            <Badge variant={correct ? "default" : "secondary"} data-testid="ipo-post-rec-correct">
              Recommendation: {correct ? "✅ Correct" : "❌ Incorrect"}
            </Badge>
          )}
        </div>
      )}
    </Section>
  );
};

const IpoDetailPage = () => {
  const { symbol } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [ipo, setIpo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verifyOpen, setVerifyOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    apiGet(`/ipo/${symbol}`)
      .then(setIpo)
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed to load IPO"))
      .finally(() => setLoading(false));
  }, [symbol]);

  const handleSaved = (updatedFinancials) => {
    setIpo((prev) => prev ? {
      ...prev,
      financials: updatedFinancials,
      pipeline: { ...(prev.pipeline || {}), admin_verified_at: new Date().toISOString() },
    } : prev);
  };

  if (loading) return <LoadingState message="Loading IPO analysis…" />;
  if (error || !ipo) return <ErrorState description={error || "IPO not found"} />;

  return (
    <div className="space-y-6" data-testid="ipo-detail-page">
      <div>
        <Button variant="ghost" size="sm" className="gap-1 -ml-2" onClick={() => navigate("/ipo")} data-testid="ipo-back-button">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to IPOs
        </Button>
      </div>

      {ipo.status === "pending_prospectus" && (
        <Card className="border-dashed" data-testid="ipo-pending-notice">
          <CardContent className="pt-6 pb-5">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" />
              <div className="font-semibold text-sm">Awaiting prospectus</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              This IPO has been registered but the prospectus has not been uploaded yet.
              Full analysis will appear here once an admin uploads the PDF.
            </p>
          </CardContent>
        </Card>
      )}

      {ipo.status === "analysing" && (
        <Card className="border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/5" data-testid="ipo-analysing-notice">
          <CardContent className="pt-6 pb-5">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[hsl(var(--info))] animate-pulse" />
              <div className="font-semibold text-sm">Claude is analysing the prospectus…</div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              This takes 3-8 minutes for a 200-400 page prospectus. Come back shortly.
            </p>
          </CardContent>
        </Card>
      )}

      <HeaderCard ipo={ipo} />
      {ipo.pipeline?.vision_extracted && (
        <Card
          className="border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5"
          data-testid="ipo-vision-notice"
        >
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start gap-2.5">
              <ScanLine className="h-4 w-4 text-[hsl(var(--warning))] mt-0.5 shrink-0" />
              <div className="space-y-1">
                <div className="font-semibold text-sm">
                  Some financials extracted via AI Vision
                </div>
                <p className="text-xs text-muted-foreground">
                  {(ipo.pipeline.vision_documents || []).length > 0 ? (
                    <>
                      The uploaded{" "}
                      <span className="font-medium">
                        {(ipo.pipeline.vision_documents || [])
                          .map((d) => d.replace(/_/g, " "))
                          .join(", ")}
                      </span>{" "}
                      PDFs were scanned images, so figures were read by
                      Claude Vision.
                    </>
                  ) : (
                    "Figures were read from scanned documents by Claude Vision."
                  )}{" "}
                  Key numbers may have minor OCR variations — always verify
                  revenue, EPS and BVPS against the original PDFs before
                  investing.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      <RecommendationCard ipo={ipo} />
      <WebResearchCard ipo={ipo} />
      <ValuationTable ipo={ipo} />
      <FinancialsTable
        ipo={ipo}
        onEditClick={isAdmin ? () => setVerifyOpen(true) : null}
        adminVerifiedAt={ipo?.pipeline?.admin_verified_at}
      />
      <SpecialistViews ipo={ipo} />
      <BusinessOverview ipo={ipo} />
      <PostListingCard ipo={ipo} />
      {isAdmin && (
        <IpoFinancialsVerifyDialog
          open={verifyOpen}
          onOpenChange={setVerifyOpen}
          ipo={ipo}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default IpoDetailPage;
